// audioCache.js – IndexedDB wrapper for caching streamed audio narration
// NOTE: Uses the low-level IndexedDB API directly – no 3rd-party deps.
// All functions return Promises and emit detailed console logs so we can
// trace behaviour in production.

const DB_NAME = 'AudioNarrationCache';
const DB_VERSION = 1;
const CHUNK_STORE = 'chunks';      // Stores individual ArrayBuffer chunks
const META_STORE  = 'meta';        // Stores small metadata docs per cacheKey

let _db = null; // IDBDatabase singleton instance

/**
 * Generate a standardized cache key for audio calls
 */
export function generateCacheKey(topic, mode, parameters, userLanguage) {
  // Normalize topic name: lowercase, remove special chars, spaces to hyphens
  const normalizedTopic = topic
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  let key = `${normalizedTopic}-${mode}-${userLanguage.toLowerCase().replace(/[^a-z]/g, '')}`;
  
  // Add parameters if present
  if (parameters && Object.keys(parameters).length > 0) {
    const paramString = Object.entries(parameters)
      .map(([k, v]) => `${k}-${v.toLowerCase().replace(/[^\w]/g, '')}`)
      .join('-');
    key += `-${paramString}`;
  }
  
  return key;
}

/**
 * Lazily open (or create) the IndexedDB database.  Subsequent calls re-use
 * the same handle.  Resolves with the IDBDatabase instance when ready.
 */
export function getDB () {
  if (_db) {
    console.debug('[AudioCache] Re-using existing DB handle');
    return Promise.resolve(_db);
  }

  console.debug(`[AudioCache] Opening IndexedDB ${DB_NAME} @ v${DB_VERSION}`);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onerror = (ev) => {
      console.error('[AudioCache] Failed to open IndexedDB', ev.target.error);
      reject(ev.target.error);
    };

    req.onupgradeneeded = (ev) => {
      const db = ev.target.result;
      console.debug('[AudioCache] Running onupgradeneeded');

      // Object store for binary chunks: keyed by [cacheKey, chunkIndex]
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        const store = db.createObjectStore(CHUNK_STORE, {
          keyPath: ['cacheKey', 'chunkIndex']
        });
        store.createIndex('byCacheKey', 'cacheKey', { unique: false });
      }

      // Store for small metadata docs – allows quick lookup of completion
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'cacheKey' });
      }
    };

    req.onsuccess = (ev) => {
      _db = ev.target.result;
      _db.onversionchange = () => {
        console.warn('[AudioCache] DB versionchange – closing handle');
        _db.close();
        _db = null;
      };
      console.debug('[AudioCache] IndexedDB ready');
      resolve(_db);
    };
  });
}

/**
 * Store a single ArrayBuffer chunk. The same cacheKey is used for the whole
 * narration; chunks are keyed by monotonic chunkIndex.
 */
export async function storeChunk (cacheKey, chunkIndex, arrayBuffer) {
  if (!arrayBuffer || arrayBuffer.byteLength === 0) {
    console.warn('[AudioCache] Ignoring empty chunk for', cacheKey, chunkIndex);
    return;
  }
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([CHUNK_STORE], 'readwrite');
    tx.onerror = (e) => {
      console.error('[AudioCache] Tx error while storing chunk', e.target.error);
      reject(e.target.error);
    };
    tx.oncomplete = () => {
      console.debug(`[AudioCache] Stored chunk #${chunkIndex} for`, cacheKey);
      resolve();
    };

    const store = tx.objectStore(CHUNK_STORE);
    store.put({ cacheKey, chunkIndex, data: arrayBuffer });
  });
}

/**
 * Mark a stream as fully cached. Also stores a timestamp for potential LRU
 * eviction strategies in future.
 */
export async function markComplete (cacheKey) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([META_STORE], 'readwrite');
    tx.onerror = (e) => {
      console.error('[AudioCache] Failed to markComplete', e.target.error);
      reject(e.target.error);
    };
    tx.oncomplete = () => {
      console.debug('[AudioCache] Stream marked complete for', cacheKey);
      resolve();
    };

    const store = tx.objectStore(META_STORE);
    store.put({ cacheKey, complete: true, cachedAt: Date.now() });
  });
}

/**
 * Retrieve *all* chunks for a given cacheKey in order.  Resolves to an array
 * of ArrayBuffers.  If the stream isn\'t fully cached (meta.complete === false
 * or missing) you still get whatever chunks exist.
 */
export async function getChunks (cacheKey) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([CHUNK_STORE], 'readonly');
    const store = tx.objectStore(CHUNK_STORE);
    const index = store.index('byCacheKey');

    const chunks = [];
    // Since our keyPath is [cacheKey, chunkIndex] and index only covers cacheKey
    // we still get items ordered by the compound key ⇒ chunkIndex ascending.
    const range = IDBKeyRange.only(cacheKey);
    index.openCursor(range).onsuccess = (ev) => {
      const cursor = ev.target.result;
      if (cursor) {
        chunks.push(cursor.value.data); // value.data is ArrayBuffer
        cursor.continue();
      }
    };

    tx.onerror = (e) => {
      console.error('[AudioCache] Tx error in getChunks', e.target.error);
      reject(e.target.error);
    };
    tx.oncomplete = () => {
      console.debug(`[AudioCache] Retrieved ${chunks.length} cached chunks for`, cacheKey);
      resolve(chunks);
    };
  });
}

/**
 * Convenience helper: check if we have a *complete* cached stream.
 */
export async function isComplete (cacheKey) {
  const db = await getDB();
  return new Promise((resolve) => {
    const tx = db.transaction([META_STORE], 'readonly');
    const store = tx.objectStore(META_STORE);
    const req = store.get(cacheKey);
    req.onsuccess = () => {
      const meta = req.result;
      console.debug('[AudioCache] meta for', cacheKey, meta);
      resolve(!!(meta && meta.complete));
    };
    req.onerror = () => {
      console.warn('[AudioCache] meta lookup error – treating as incomplete');
      resolve(false);
    };
  });
} 